import React, {useMemo} from "react";
import {Tag} from "primereact/tag";
import {formatFocusTime} from "../../pages/dashboard/focus-timer";
import {timestampToDate} from "../utils";

function validTime(value) {
    const time = new Date(value || "").getTime();
    return Number.isFinite(time) ? time : null;
}

export function normalizeFocusSegments(session) {
    const segments = Array.isArray(session?.activitySegments) ? session.activitySegments : [];
    return segments.map((segment) => ({
        startedAt: validTime(segment?.startedAt),
        endedAt: validTime(segment?.endedAt),
    })).filter((segment) => segment.startedAt !== null);
}

export function focusSessionEvents(session) {
    return normalizeFocusSegments(session).flatMap((segment, index) => [
        {key: `start-${index}-${segment.startedAt}`, type: "started", at: segment.startedAt},
        ...(segment.endedAt !== null
            ? [{key: `pause-${index}-${segment.endedAt}`, type: "paused", at: segment.endedAt}]
            : []),
    ]).sort((left, right) => right.at - left.at);
}

export function focusBreakSeconds(session, nowMs = Date.now()) {
    const segments = normalizeFocusSegments(session);
    let breakMilliseconds = 0;

    for (let index = 0; index < segments.length - 1; index += 1) {
        const endedAt = segments[index].endedAt;
        const nextStartedAt = segments[index + 1].startedAt;
        if (endedAt !== null && nextStartedAt > endedAt) {
            breakMilliseconds += nextStartedAt - endedAt;
        }
    }

    if (session?.focusSessionStatus === "paused") {
        const lastSegment = segments[segments.length - 1];
        const pausedAt = lastSegment?.endedAt ?? validTime(session?.pausedAt);
        if (pausedAt !== null && Number(nowMs) > pausedAt) {
            breakMilliseconds += Number(nowMs) - pausedAt;
        }
    }

    return Math.max(0, Math.floor(breakMilliseconds / 1000));
}

function formatEventTime(milliseconds) {
    return timestampToDate(Math.floor(milliseconds / 1000), "HH:mm:ss");
}

export default function FocusSessionHistory({session, translate, nowMs = Date.now()}) {
    const events = useMemo(() => focusSessionEvents(session), [session]);
    const breakSeconds = focusBreakSeconds(session, nowMs);
    if (!events.length) return null;

    return <div className="border-top-1 border-100 pt-2 flex flex-column gap-2">
        <div className="flex align-items-center justify-content-between gap-2">
            <span className="text-xs font-bold text-700">{translate("focus.history.title")}</span>
            <Tag
                className="text-xs gap-2"
                severity="secondary"
                icon="pi pi-coffee"
                value={`${translate("focus.history.break-total")}: ${formatFocusTime(breakSeconds)}`}
            />
        </div>
        <div className="flex flex-column gap-1 overflow-auto">
            {events.map((event) => <div key={event.key} className="surface-card border-1 border-100 border-round-lg px-2 py-1 flex align-items-center justify-content-between gap-2">
                <span className="flex align-items-center gap-2 text-xs text-700">
                    <i className={`pi ${event.type === "started" ? "pi-play text-green-500" : "pi-pause text-orange-500"}`}/>
                    {translate(`focus.history.${event.type}`)}
                </span>
                <time className="ltr text-xs text-500 white-space-nowrap">{formatEventTime(event.at)}</time>
            </div>)}
        </div>
    </div>;
}
